import React, { useState } from "react";
import {
  Button,
  Input,
  Upload,
  Modal,
  message,
  Typography,
  Switch,
  Radio,
  InputNumber,
  Image,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { Form } from "@ant-design/compatible";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { useMutation } from "@apollo/client";
import {
  CREATE_LOYALTY,
  CREATE_LOYALTY_SUCCESS,
  LIST_LOYALTIES,
} from "../../constants";

import "../../index.scss";
import strings from "../../../../../../../../strings";

const CreateLoyaltyModal = ({ visible, closeModal, path, form, user }) => {
  const [state, setstate] = useState({
    imageUrl: "",
    loading: false,
  });
  const [showExpiration, setShowExpiration] = useState(false);
  const [loading, setLoading] = useState(false);
  const [amountValue, setAmountValue] = useState(null);
  const [pointsValue, setPointsValue] = useState(null);
  const [showDecimalWarning, setShowDecimalWarning] = useState(false);

  const history = useHistory();

  const { getFieldDecorator } = form;

  const [createLoyalty, { loading: isCreateLoyaltyLoading }] = useMutation(
    CREATE_LOYALTY,
    {
      onCompleted: data => {
        form.resetFields();

        data.createLoyalty &&
          Modal.success({
            title: CREATE_LOYALTY_SUCCESS,
            okText: strings.createTier,
            onOk: () => {
              closeModal();

              history.push({
                pathname: `${path}/${data.createLoyalty.id}`,
                state: { isFromCreateLoyalty: true },
              });
            },
            okButtonProps: {
              id: "btn-component",
            },
          });
      },
      onError: error => {
        message.error(error.message, 5);
      },
      refetchQueries: [{ query: LIST_LOYALTIES }],
    }
  );

  const onCreate = async values => {
    if (state.imageUrl) {
      await createLoyalty({
        variables: {
          name: values.name,
          image_url: state.imageUrl,
          currency_value: Number(values.amount),
          points: Number(values.points),
          verification: values.verification,
          expiration: Number(values.expiration),
          // magnification: Number(values.magnification)
        },
      });
    }
  };

  // Check if the points-to-currency ratio will result in decimal points for customers
  const checkForDecimals = (amount, points) => {
    if (!amount || !points || amount <= 0 || points <= 0) {
      setShowDecimalWarning(false);
      return;
    }

    // Check if dividing any purchase amount by the ratio would create decimals
    // Example: if ratio is 100 currency = 3 points, then 1 currency = 0.03 points (decimal)
    // We want: points % amount === 0 OR amount % points === 0 for whole numbers
    
    // Check if the ratio creates whole number points per currency unit
    const pointsPerCurrency = points / amount;
    const hasDecimal = !Number.isInteger(pointsPerCurrency) && pointsPerCurrency < 1;
    
    // Also check if currency per point is a clean division
    const currencyPerPoint = amount / points;
    const currencyHasDecimal = !Number.isInteger(currencyPerPoint) && currencyPerPoint < 1;
    
    setShowDecimalWarning(hasDecimal || currencyHasDecimal);
  };

  const handleAmountChange = (value) => {
    setAmountValue(value);
    checkForDecimals(value, pointsValue);
  };

  const handlePointsChange = (value) => {
    setPointsValue(value);
    checkForDecimals(amountValue, value);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      await onCreate(values);
    } catch (error) {
      console.log("Validate Failed:", error);
    }
  };

  const normFile = e => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const uploadProps = {
    name: "image",
    action: `${process.env.REACT_APP_MULTI_LEVEL_BASE_URL}/upload`,
    onChange(info) {
      setLoading(true);
      if (info.file.status === "done") {
        message.success(`${info.file.name} ${strings.fileUploadSuccessful}`);
        setstate({ ...state, imageUrl: info.file.response.imageCloudData.secure_url });
        setLoading(false);
      } else if (info.file.status === "error") {
        message.error(`${info.file.name} ${strings.fileUploadFailed}`);
        setLoading(false);
      }
    },
  };

  return (
    <Modal
      title={strings.createLoyaltyProgram}
      className="create_loyalty_modal"
      open={visible}
      onCancel={closeModal}
      footer={[
        <Button key="cancel" onClick={closeModal}>
          {strings.cancel}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isCreateLoyaltyLoading}
          id="btn-component"
          onClick={() => handleSubmit()}
        >
          {strings.createLoyalty}
        </Button>,
      ]}
    >
      <Form layout="vertical" name="create_loyalty" id="loyaltyForm">
        <Form.Item name="name" label={strings.name} className="wrap">
          {getFieldDecorator("name", {
            rules: [{ required: true }],
          })(<Input />)}
        </Form.Item>

        {/* <Form.Item name="magnification" label={strings.magnification} className="wrap">
          {getFieldDecorator("magnification", {
            rules: [{ required: true }],
          })(<InputNumber style={{width: "100%"}} />)}
        </Form.Item> */}

        {state.imageUrl && <Image src={state.imageUrl} style={{height:100, width: 100}} preview alt="loyalty" />}
        <Form.Item
          name="upload"
          label={strings.loyaltyArtwork}
          className="wrap"
        >
          {getFieldDecorator("upload", {
            rules: [{ required: true }],
            valuePropName: "fileList",
            getValueFromEvent: normFile,
          })(
            <Upload 
              listType="picture" 
              multiple={false} {...uploadProps}
            >
              <Button icon={<UploadOutlined />} loading={loading}>{strings.clickToUpload}</Button>
            </Upload>
          )}
        </Form.Item>
        
        <div >
        <Typography.Text>
          <p>{strings.amountToPointsRatio}</p>
        </Typography.Text>
        </div>
       

        <div className="curr_points_ratio">
          <Form.Item
            name="amount"
            label={strings.amount + ` ${user.currency}`}
            className="wrap"
          >
            {getFieldDecorator("amount", {
              rules: [{ required: true }],
            })(<InputNumber style={{width:"100%"}} onChange={handleAmountChange} />)}
          </Form.Item>

          <span className="equal_sign">===</span>

          <Form.Item name="points" label={strings.points} className="wrap">
            {getFieldDecorator("points", {
              rules: [{ required: true }],
            })(<InputNumber style={{width:"100%"}} onChange={handlePointsChange} />)}
          </Form.Item>
        </div>
        
        {showDecimalWarning && (
          <div style={{ 
            marginBottom: 16, 
            padding: '12px 16px', 
            background: '#fff7e6', 
            border: '1px solid #ffd666',
            borderRadius: '4px'
          }}>
            <Typography.Text style={{ color: '#d48806', fontWeight: 500 }}>
              ⚠️ Warning: This ratio may result in decimal points for customer balances
            </Typography.Text>
            <br />
            <Typography.Text style={{ color: '#8c8c8c', fontSize: '13px' }}>
              With {amountValue} {user.currency} = {pointsValue} points, customers will earn {(pointsValue / amountValue).toFixed(4)} points per {user.currency} spent, which may create fractional points.
              <br />
              <strong>Best Practice:</strong> Use whole-number ratios like 1:1, 10:1, 100:10, or 1:10 for easier customer understanding and better engagement.
            </Typography.Text>
          </div>
        )}
        
        <Typography.Text  style={{marginBottom:20, display: "inline-block"}}>
          {/* {strings.recommend1 + user.currency + strings.recommend2 + user.currency + strings.recommend3} */}
          {strings.forBestLoyaltyExperience}
        </Typography.Text>

        <Form.Item
          name="verification"
          label="Verify With Token"
          className="wrap mt-2"
        >
          <Typography.Text>
            Verify with token before redeeming points{" "}
          </Typography.Text>
          {getFieldDecorator("verification")(<Switch />)}
        </Form.Item>

        <div>
          <Radio.Group
            onChange={val => {
              val.target.value === 1
                ? setShowExpiration(false)
                : setShowExpiration(true);
            }}
          >
            <Radio defaultChecked={true} value={1}>
              Points do not expire
            </Radio>
            <Radio value={2}>Points expire</Radio>
          </Radio.Group>
        </div>

        {showExpiration && (
          <div className="mt-2">
            <Form.Item
              name="expiration"
              label="Set Points Expiration"
              className="wrap mt-2"
            >
              {getFieldDecorator("expiration")(
                <Input
                  name="expiration"
                  type="number"
                  min={1}
                  style={{ width: "60px" }}
                />
              )}
              <Typography.Text>Days until point expiry</Typography.Text>
            </Form.Item>
          </div>
        )}
      </Form>
    </Modal>
  );
};

CreateLoyaltyModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
};

export default Form.create()(CreateLoyaltyModal);
